import { defineStore } from 'pinia';
import { ref } from 'vue';
import Message from '@/utils/message';
import {
  listWorkflowsApi,
  deleteWorkflowApi,
  getWorkflowApi,
} from '@/api/workflow';
import {
  bindWorkflowTagApi,
  listWorkflowTagsApi,
  unbindWorkflowTagApi,
} from '@/api/workflow-tag';
import type {
  WorkflowTableData,
  WorkflowTableFilter,
  NodeData,
  NodeVarsGroup,
} from '@/types/workflow';
import type { WorkflowTag } from '@/types/workflow-tag';
import cloneDeep from 'lodash/cloneDeep';
import i18n from '@/locale';
import useUserStore from '@/store/modules/user';

/**
 * 工作流状态接口
 */
export interface WorkflowState {
  // 工作流列表
  workflowList: WorkflowTableData[];
  loading: boolean;
  total: number;
  currentPage: number;
  pageSize: number;

  // 搜索筛选
  searchParams: WorkflowTableFilter;
  searchLoading: boolean;
  availableTags: WorkflowTag[];
  tagLoading: boolean;
  selectedTagUuids: string[];

  // 模态框状态
  workflowModalVisible: boolean;
  importWorkflowModalVisible: boolean;
  manageTagsVisible: boolean;
  editInfo: WorkflowTableData | undefined;
  cloneWorkflowRecord: WorkflowTableData | undefined;

  // 响应式 UI
  isCollapsed: boolean;
  isCollapsible: boolean;

  // 操作状态
  btnLoadingArr: string[];
}

/**
 * 工作流 Store
 */
export const useWorkflowStore = defineStore('workflow', () => {
  const userStore = useUserStore();

  // ==================== 状态定义 ====================

  // 工作流列表状态
  const workflowList = ref<WorkflowTableData[]>([]);
  const loading = ref<boolean>(false);
  const total = ref<number>(0);
  const currentPage = ref<number>(1);
  const pageSize = ref<number>(20);

  // 工作流配置相关状态
  const selectedNodeData = ref();
  const fullscreenInputPanelVisible = ref(false);
  const fullscreenInputType = ref<
    'code' | 'system_prompt' | 'prompt' | 'user_prompt' | 'exception_handle'
  >('code');
  const predecessorVars = ref<NodeVarsGroup[]>([]);
  const watchedFields = ref<string[]>([]);

  // 搜索筛选状态
  const searchParams = ref<WorkflowTableFilter>({
    catalog: '',
    name: '',
    user_uuids: [],
    tag_uuids: [],
  });
  const searchLoading = ref<boolean>(false);
  const availableTags = ref<WorkflowTag[]>([]);
  const tagLoading = ref<boolean>(false);
  const selectedTagUuids = ref<string[]>([]);

  // 模态框状态
  const workflowModalVisible = ref<boolean>(false);
  const importWorkflowModalVisible = ref<boolean>(false);
  const manageTagsVisible = ref<boolean>(false);
  const editInfo = ref<WorkflowTableData | undefined>(undefined);
  const cloneWorkflowRecord = ref<WorkflowTableData | undefined>(undefined);

  // 响应式 UI 状态
  const isCollapsed = ref<boolean>(false);
  const isCollapsible = ref<boolean>(false);

  // 操作状态
  const btnLoadingArr = ref<string[]>([]);

  // ==================== 数据加载方法 ====================

  /**
   * 加载标签列表
   */
  const loadWorkflowTags = async (): Promise<void> => {
    try {
      tagLoading.value = true;
      const response = await listWorkflowTagsApi({
        tenant_uuid: userStore.tenantUuid || '',
        workspace_uuid: userStore.workspaceUuid || '',
        pagination: {
          page_no: 1,
          page_size: 1000,
        },
      });
      availableTags.value = response.data.results || [];
      const validSelectedTagUuids = selectedTagUuids.value.filter((tagUuid) =>
        availableTags.value.some((tag) => tag.tag_uuid === tagUuid)
      );
      if (validSelectedTagUuids.length !== selectedTagUuids.value.length) {
        selectedTagUuids.value = validSelectedTagUuids;
        searchParams.value.tag_uuids = [...validSelectedTagUuids];
      }
    } catch {
      availableTags.value = [];
    } finally {
      tagLoading.value = false;
    }
  };

  /**
   * 加载工作流列表
   */
  const loadWorkflowList = async (page?: number): Promise<void> => {
    try {
      loading.value = true;
      if (page) {
        currentPage.value = page;
      }

      const params = {
        workspace_uuid: userStore.workspaceUuid || '',
        tenant_uuid: userStore.tenantUuid || '',
        filters: {
          catalog: '',
          name: searchParams.value.name,
          user_uuids: searchParams.value.user_uuids,
          tag_uuids: selectedTagUuids.value,
        },
        pagination: {
          page_no: currentPage.value,
          page_size: pageSize.value,
        },
      };

      const response = await listWorkflowsApi(params);
      const { data: resData } = response;

      workflowList.value = resData.results || [];
      total.value = resData.total_count || 0;
    } catch {
      workflowList.value = [];
    } finally {
      loading.value = false;
    }
  };

  /**
   * 初始化数据
   */
  const initializeData = async (): Promise<void> => {
    await Promise.all([loadWorkflowList(1), loadWorkflowTags()]);
  };

  // ==================== 工作流操作方法 ====================

  /**
   * 删除工作流
   */
  const deleteWorkflow = async (workflow_uuid: string): Promise<void> => {
    try {
      btnLoadingArr.value.push(workflow_uuid);
      await deleteWorkflowApi({
        tenant_uuid: userStore.tenantUuid || '',
        workspace_uuid: userStore.workspaceUuid || '',
        workflow_uuid,
      });
      await loadWorkflowList();
    } catch (error) {
      console.error('删除工作流失败:', error);
      Message.error(i18n.global.t('messages.deleteFailed'));
    } finally {
      const index = btnLoadingArr.value.indexOf(workflow_uuid);
      if (index > -1) {
        btnLoadingArr.value.splice(index, 1);
      }
    }
  };

  /**
   * 设置标签筛选
   */
  const setSelectedTagUuids = (tagUuids: string[]): void => {
    selectedTagUuids.value = [...tagUuids];
    searchParams.value.tag_uuids = [...tagUuids];
    loadWorkflowList(1);
  };

  /**
   * 设置成员筛选
   */
  const setUserUuidFilter = (userUuids: string[] = []): void => {
    searchParams.value.user_uuids = [...userUuids];
    loadWorkflowList(1);
  };

  /**
   * 设置标签管理弹窗可见性
   */
  const setManageTagsVisible = (visible: boolean): void => {
    manageTagsVisible.value = visible;
  };

  /**
   * 同步本地工作流标签
   */
  const syncWorkflowTags = (
    workflow_uuid: string,
    nextTags: WorkflowTag[]
  ): void => {
    workflowList.value = workflowList.value.map((item) => {
      if (item.workflow_uuid !== workflow_uuid) {
        return item;
      }
      return {
        ...item,
        tags: nextTags,
      };
    });

    if (editInfo.value?.workflow_uuid === workflow_uuid) {
      editInfo.value = {
        ...editInfo.value,
        tags: nextTags,
      };
    }
  };

  /**
   * 绑定标签
   */
  const bindWorkflowTag = async (
    workflow_uuid: string,
    tag_uuid: string
  ): Promise<void> => {
    await bindWorkflowTagApi({
      tenant_uuid: userStore.tenantUuid || '',
      workspace_uuid: userStore.workspaceUuid || '',
      workflow_uuid,
      tag_uuid,
    });
  };

  /**
   * 解绑标签
   */
  const unbindWorkflowTag = async (
    workflow_uuid: string,
    tag_uuid: string
  ): Promise<void> => {
    await unbindWorkflowTagApi({
      tenant_uuid: userStore.tenantUuid || '',
      workspace_uuid: userStore.workspaceUuid || '',
      workflow_uuid,
      tag_uuid,
    });
  };

  /**
   * 按差异同步工作流标签
   */
  const syncWorkflowTagBindings = async (
    workflow_uuid: string,
    nextTagUuids: string[],
    currentTagUuids: string[] = []
  ): Promise<void> => {
    const toBind = nextTagUuids.filter(
      (tagUuid) => !currentTagUuids.includes(tagUuid)
    );
    const toUnbind = currentTagUuids.filter(
      (tagUuid) => !nextTagUuids.includes(tagUuid)
    );

    if (!toBind.length && !toUnbind.length) {
      return;
    }

    await Promise.all([
      ...toBind.map((tagUuid) => bindWorkflowTag(workflow_uuid, tagUuid)),
      ...toUnbind.map((tagUuid) => unbindWorkflowTag(workflow_uuid, tagUuid)),
    ]);

    const nextTags = availableTags.value.filter((tag) =>
      nextTagUuids.includes(tag.tag_uuid)
    );
    syncWorkflowTags(workflow_uuid, nextTags);
  };

  /**
   * 获取工作流详情
   */
  const getWorkflowInfo = async (
    workflow_uuid: string
  ): Promise<WorkflowTableData> => {
    try {
      const response = await getWorkflowApi({
        tenant_uuid: userStore.tenantUuid || '',
        workspace_uuid: userStore.workspaceUuid || '',
        workflow_uuid,
      });
      return response.data.workflow;
    } catch (error) {
      console.error('获取工作流详情失败:', error);
      Message.error(i18n.global.t('messages.getWorkflowDetailFailed'));
      throw error;
    }
  };

  /**
   * 选择工作流节点
   */
  const setSelectedNodeData = (data: NodeData) => {
    selectedNodeData.value = cloneDeep(data);
  };

  /**
   * 设置前置节点变量
   */
  const setPredecessorVars = (vars: NodeVarsGroup[]) => {
    predecessorVars.value = vars;
  };

  /**
   * 设置监听的字段
   */
  const setWatchedFields = (fields: string[]) => {
    watchedFields.value = fields;
  };

  // ==================== 搜索和分页方法 ====================

  /**
   * 搜索工作流
   */
  const searchWorkflows = (searchValue: string): void => {
    searchParams.value.name = searchValue;
    loadWorkflowList(1);
  };

  /**
   * 设置页码
   */
  const setPage = (page: number): void => {
    currentPage.value = page;
    loadWorkflowList();
  };

  /**
   * 设置每页大小
   */
  const setPageSize = (size: number): void => {
    pageSize.value = size;
    loadWorkflowList(1);
  };

  /**
   * 刷新数据
   */
  const refreshData = async (): Promise<void> => {
    await Promise.all([loadWorkflowList(), loadWorkflowTags()]);
  };

  // ==================== UI 状态管理方法 ====================

  /**
   * 设置工作流模态框可见性
   */
  const setWorkflowModalVisible = (visible: boolean): void => {
    workflowModalVisible.value = visible;
  };

  /**
   * 设置导入工作流模态框可见性
   */
  const setImportWorkflowModalVisible = (visible: boolean): void => {
    importWorkflowModalVisible.value = visible;
  };

  /**
   * 设置编辑信息
   */
  const setEditInfo = (info: WorkflowTableData | undefined): void => {
    editInfo.value = info;
  };

  /**
   * 设置克隆工作流记录
   */
  const setCloneWorkflowRecord = (
    record: WorkflowTableData | undefined
  ): void => {
    cloneWorkflowRecord.value = record;
  };

  /**
   * 设置折叠状态
   */
  const setCollapsed = (collapsed: boolean): void => {
    isCollapsed.value = collapsed;
  };

  /**
   * 设置可折叠状态
   */
  const setCollapsible = (collapsible: boolean): void => {
    isCollapsible.value = collapsible;
  };

  /**
   * 切换折叠状态
   */
  const toggleCollapse = (): void => {
    isCollapsed.value = !isCollapsed.value;
  };

  /**
   * 设置全屏编辑状态
   * @param visible
   */
  const setFullscreenInputPanelVisible = (visible: boolean) => {
    fullscreenInputPanelVisible.value = visible;
  };
  const setFullscreenInputType = (
    type:
      | 'code'
      | 'system_prompt'
      | 'prompt'
      | 'user_prompt'
      | 'exception_handle'
  ) => {
    fullscreenInputType.value = type;
  };

  return {
    // 状态
    workflowList,
    loading,
    total,
    currentPage,
    pageSize,
    searchParams,
    searchLoading,
    availableTags,
    tagLoading,
    selectedTagUuids,
    workflowModalVisible,
    importWorkflowModalVisible,
    manageTagsVisible,
    editInfo,
    cloneWorkflowRecord,
    isCollapsed,
    isCollapsible,
    btnLoadingArr,
    selectedNodeData,
    watchedFields,
    fullscreenInputPanelVisible,
    fullscreenInputType,
    predecessorVars,

    // 方法
    loadWorkflowTags,
    loadWorkflowList,
    initializeData,
    deleteWorkflow,
    bindWorkflowTag,
    unbindWorkflowTag,
    syncWorkflowTagBindings,
    syncWorkflowTags,
    getWorkflowInfo,
    searchWorkflows,
    setSelectedTagUuids,
    setUserUuidFilter,
    setPage,
    setPageSize,
    refreshData,
    setWorkflowModalVisible,
    setImportWorkflowModalVisible,
    setManageTagsVisible,
    setEditInfo,
    setCloneWorkflowRecord,
    setCollapsed,
    setCollapsible,
    toggleCollapse,
    setSelectedNodeData,
    setWatchedFields,
    setFullscreenInputPanelVisible,
    setFullscreenInputType,
    setPredecessorVars,
  };
});

export default useWorkflowStore;
